import React, { useState, useEffect } from 'react';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import axios from 'axios';

const ImportSalarySettings = ({
    show,
    onHide,
    onImport,
    canImportSalary,
    error,
    setError,
    API_BASE
}) => {
    const [csvData, setCsvData] = useState([]);
    const [importProgress, setImportProgress] = useState(0);
    const [isImporting, setIsImporting] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [users, setUsers] = useState([]);

    // All available fields in salary_settings table
    const availableFields = [
        { name: 'emp_code', label: 'Employee Code', type: 'text', required: true, description: 'Employee code (must exist in system)' },
        { name: 'shift_id', label: 'Shift ID', type: 'number', required: false, description: 'Shift assignment ID' },
        { name: 'policy_id', label: 'Policy ID', type: 'number', required: false, description: 'Salary policy ID' },
        { name: 'template_id', label: 'Template ID', type: 'number', required: false, description: 'Salary template ID' },
        { name: 'leave_approve_by', label: 'Leave Approver ID', type: 'number', required: false, description: 'User ID who approves leaves' },
        { name: 'weeklyoff_1', label: 'Weekly Off 1', type: 'enum', required: false, description: 'First weekly off day', options: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] },
        { name: 'weeklyoff_2', label: 'Weekly Off 2', type: 'enum', required: false, description: 'Second weekly off day', options: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] },
        { name: 'salary_type', label: 'Salary Type', type: 'enum', required: true, description: 'Type of salary calculation', options: ['Monthly', 'Daily', 'Hourly', 'Piece'] },
        { name: 'salary_value', label: 'Salary Value', type: 'decimal', required: false, description: 'Salary amount' },
        { name: 'eligible_for_pf', label: 'Eligible for PF', type: 'boolean', required: true, description: 'Eligible for Provident Fund (1=Yes, 0=No)' },
        { name: 'pf_percentage', label: 'PF Percentage', type: 'decimal', required: true, description: 'Employee PF contribution %' },
        { name: 'pf_employer_percentage', label: 'PF Employer %', type: 'decimal', required: true, description: 'Employer PF contribution %' },
        { name: 'pf_ceiling', label: 'PF Ceiling', type: 'decimal', required: true, description: 'PF calculation ceiling amount' },
        { name: 'eligible_for_esi', label: 'Eligible for ESI', type: 'boolean', required: true, description: 'Eligible for ESI (1=Yes, 0=No)' },
        { name: 'esi_percentage', label: 'ESI Percentage', type: 'decimal', required: true, description: 'Employee ESI contribution %' },
        { name: 'esi_employer_percentage', label: 'ESI Employer %', type: 'decimal', required: true, description: 'Employer ESI contribution %' },
        { name: 'esi_ceiling', label: 'ESI Ceiling', type: 'decimal', required: true, description: 'ESI calculation ceiling amount' },
        { name: 'min_hour_for_present', label: 'Min Hours for Present', type: 'decimal', required: true, description: 'Minimum hours to mark present' },
        { name: 'ot_hour_beyond_wh', label: 'OT Hours Beyond WH', type: 'decimal', required: true, description: 'Overtime hours beyond working hours' },
        { name: 'present_days_type', label: 'Present Days Type', type: 'enum', required: true, description: 'Method to calculate present days', options: ['Standard', 'SalaryMonth', 'FullMonth'] },
        { name: 'standard_days', label: 'Standard Days', type: 'number', required: true, description: 'Standard working days per month' },
        { name: 'it_applicable', label: 'IT Applicable', type: 'enum', required: true, description: 'Income Tax applicable', options: ['Yes', 'No'] },
        { name: 'it_rule', label: 'IT Rule', type: 'text', required: true, description: 'Income Tax rule configuration' },
        { name: 'permission_per_month', label: 'Permission per Month', type: 'number', required: true, description: 'Number of permissions allowed per month' },
        { name: 'permission_hour', label: 'Permission Hours', type: 'decimal', required: true, description: 'Hours allowed per permission' },
        { name: 'onduty_per_month', label: 'On-Duty per Month', type: 'number', required: true, description: 'Number of on-duty days per month' },
        { name: 'auto_punch', label: 'Auto Punch', type: 'boolean', required: false, description: 'Allow auto punch (1=Yes, 0=No)' }
    ];

    // Fetch employees for validation
    const fetchEmployees = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');

            const response = await axios.get(
                `${API_BASE}/get_employees.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setEmployees(response.data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }, [API_BASE]);

    // Fetch shifts
    const fetchShifts = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `${API_BASE}/get_shifts.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.data.success) {
                setShifts(response.data.shifts || []);
            }
        } catch (error) {
            console.error('Error fetching shifts:', error);
        }
    }, [API_BASE]);

    // Fetch salary templates
    const fetchTemplates = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `${API_BASE}/get_salary_templates.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.data.success) {
                setTemplates(response.data.templates || []);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    }, [API_BASE]);

    // Fetch users (for leave approvers)
    const fetchUsers = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `${API_BASE}/get_users.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.data.success) {
                setUsers(response.data.users || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, [API_BASE]);

    useEffect(() => {
        if (show) {
            fetchEmployees();
            fetchShifts();
            fetchTemplates();
            fetchUsers();
        }
    }, [show, fetchEmployees, fetchShifts, fetchTemplates, fetchUsers]);

    const downloadTemplate = () => {
        if (selectedFields.length === 0) {
            alert('Please select at least one field to include in the template');
            return;
        }

        // Sort fields to ensure emp_code is first
        const sortedFields = [
            ...selectedFields.filter(f => f.name === 'emp_code'),
            ...selectedFields.filter(f => f.name !== 'emp_code')
        ];

        // Get employee codes for sample data
        const employeeSamples = employees
            .filter(emp => emp.status === 'Active')
            .slice(0, 5)
            .map(emp => ({
                emp_code: emp.emp_code,
                emp_name: emp.first_name + ' ' + (emp.last_name || '')
            }));

        // Create template data structure
        const headerRow = sortedFields.map(field => field.name);
        
        // Create sample data with meaningful values
        const sampleData = employeeSamples.map((emp, idx) => {
            const row = {};
            sortedFields.forEach(field => {
                switch (field.name) {
                    case 'emp_code':
                        row[field.name] = emp.emp_code;
                        break;
                    case 'shift_id':
                        row[field.name] = shifts.length > 0 ? shifts[0].shift_id : 1;
                        break;
                    case 'policy_id':
                        row[field.name] = 0; // Default policy ID
                        break;
                    case 'template_id':
                        row[field.name] = templates.length > 0 ? templates[0].template_id : '';
                        break;
                    case 'leave_approve_by':
                        row[field.name] = users.length > 0 ? users[0].user_id : 1;
                        break;
                    case 'weeklyoff_1':
                        row[field.name] = 'Sunday';
                        break;
                    case 'weeklyoff_2':
                        row[field.name] = idx % 2 === 0 ? 'Saturday' : '';
                        break;
                    case 'salary_type':
                        row[field.name] = 'Monthly';
                        break;
                    case 'salary_value':
                        row[field.name] = 25000 + (idx * 5000);
                        break;
                    case 'eligible_for_pf':
                        row[field.name] = 1;
                        break;
                    case 'pf_percentage':
                        row[field.name] = 12.00;
                        break;
                    case 'pf_employer_percentage':
                        row[field.name] = 13.00;
                        break;
                    case 'pf_ceiling':
                        row[field.name] = 15000.00;
                        break;
                    case 'eligible_for_esi':
                        row[field.name] = 1;
                        break;
                    case 'esi_percentage':
                        row[field.name] = 1.75;
                        break;
                    case 'esi_employer_percentage':
                        row[field.name] = 4.75;
                        break;
                    case 'esi_ceiling':
                        row[field.name] = 21000.00;
                        break;
                    case 'min_hour_for_present':
                        row[field.name] = 8.0;
                        break;
                    case 'ot_hour_beyond_wh':
                        row[field.name] = 9.0;
                        break;
                    case 'present_days_type':
                        row[field.name] = 'SalaryMonth';
                        break;
                    case 'standard_days':
                        row[field.name] = 26;
                        break;
                    case 'it_applicable':
                        row[field.name] = 'No';
                        break;
                    case 'it_rule':
                        row[field.name] = '';
                        break;
                    case 'permission_per_month':
                        row[field.name] = 3;
                        break;
                    case 'permission_hour':
                        row[field.name] = 2.00;
                        break;
                    case 'onduty_per_month':
                        row[field.name] = 0;
                        break;
                    case 'auto_punch':
                        row[field.name] = 0;
                        break;
                    default:
                        row[field.name] = '';
                }
            });
            return row;
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(sampleData, { header: headerRow });

        // Set column widths
        const wscols = sortedFields.map(field => {
            let width = 15;
            if (field.name === 'emp_code') width = 18;
            if (field.name.includes('percentage')) width = 12;
            if (field.name.includes('value') || field.name.includes('ceiling')) width = 15;
            if (field.name.includes('rule')) width = 30;
            return { wch: width };
        });
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salary Settings");

        // Add instructions sheet
        const instructions = [
            ['SALARY SETTINGS IMPORT TEMPLATE - INSTRUCTIONS'],
            [''],
            ['HOW TO FILL THIS TEMPLATE:'],
            ['1. Fill values in the appropriate columns for each employee'],
            ['2. emp_code is required and must exist in the system'],
            ['3. For boolean fields (eligible_for_pf, eligible_for_esi, auto_punch): Use 1 for Yes, 0 for No'],
            ['4. For enum fields, use exactly the values shown in the Options column'],
            ['5. Leave cells blank if no value for that field'],
            ['6. Do not modify the column headers'],
            [''],
            ['COLUMN DESCRIPTIONS:'],
            ['Field', 'Required', 'Type', 'Options/Format', 'Description']
        ];

        sortedFields.forEach(field => {
            let options = '';
            if (field.options) {
                options = field.options.join(', ');
            } else if (field.type === 'boolean') {
                options = '1 (Yes), 0 (No)';
            }
            
            instructions.push([
                field.name,
                field.required ? 'Yes' : 'No',
                field.type,
                options,
                field.description
            ]);
        });

        instructions.push(
            [''],
            ['IMPORTANT NOTES:'],
            ['1. Do not change column names'],
            ['2. Do not add or remove columns from the template'],
            ['3. Ensure employee codes are correct'],
            ['4. Save file as CSV or Excel before importing'],
            ['5. Maximum file size: 5MB'],
            ['6. Importing will INSERT new records or UPDATE existing ones based on emp_code']
        );

        const ws2 = XLSX.utils.aoa_to_sheet(instructions);
        ws2['!cols'] = [
            { wch: 20 },
            { wch: 10 },
            { wch: 15 },
            { wch: 40 },
            { wch: 50 }
        ];
        XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

        // Add reference data sheet
        const referenceData = [
            ['REFERENCE DATA'],
            [''],
            ['EMPLOYEES:'],
            ['emp_id', 'emp_code', 'emp_name', 'status']
        ];

        employees.slice(0, 20).forEach(emp => {
            referenceData.push([
                emp.emp_id,
                emp.emp_code,
                `${emp.first_name} ${emp.last_name || ''}`,
                emp.status || 'Active'
            ]);
        });

        referenceData.push(
            [''],
            ['SHIFTS:'],
            ['shift_id', 'shift_name']
        );

        shifts.slice(0, 10).forEach(shift => {
            referenceData.push([
                shift.shift_id,
                shift.shift_name || `Shift ${shift.shift_id}`
            ]);
        });

        referenceData.push(
            [''],
            ['SALARY TEMPLATES:'],
            ['template_id', 'template_name']
        );

        templates.slice(0, 10).forEach(template => {
            referenceData.push([
                template.template_id,
                template.template_name || `Template ${template.template_id}`
            ]);
        });

        referenceData.push(
            [''],
            ['USERS (Leave Approvers):'],
            ['user_id', 'username', 'name']
        );

        users.slice(0, 10).forEach(user => {
            referenceData.push([
                user.user_id,
                user.username || '',
                `${user.first_name || ''} ${user.last_name || ''}`
            ]);
        });

        const ws3 = XLSX.utils.aoa_to_sheet(referenceData);
        ws3['!cols'] = [
            { wch: 12 },
            { wch: 20 },
            { wch: 30 },
            { wch: 15 }
        ];
        XLSX.utils.book_append_sheet(wb, ws3, "Reference Data");

        // Save file
        const fileName = `salary_settings_template_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const handleFileUpload = (event) => {
        if (!canImportSalary) {
            alert('You do not have permission to import salary settings');
            return;
        }

        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name.toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

        if (isExcel) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { raw: false });

                    if (jsonData && jsonData.length > 0) {
                        setCsvData(jsonData);
                    } else {
                        setError('No valid data found in Excel file');
                    }
                } catch (error) {
                    setError('Error reading Excel file: ' + error.message);
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            Papa.parse(file, {
                header: true,
                complete: (results) => {
                    if (results.data && results.data.length > 0) {
                        setCsvData(results.data);
                    } else {
                        setError('No valid data found in CSV file');
                    }
                },
                error: (error) => {
                    setError('Error parsing CSV file: ' + error.message);
                }
            });
        }
    };

    const handleImport = async () => {
        if (!canImportSalary) {
            alert('You do not have permission to import salary settings');
            return;
        }

        if (!csvData.length) {
            setError('No data to import');
            return;
        }

        setIsImporting(true);
        setImportProgress(0);
        setError('');

        try {
            await onImport(csvData, setImportProgress);
            onHide();
            setCsvData([]);
            setSelectedFields([]);
        } catch (error) {
            setError('Import failed: ' + error.message);
        } finally {
            setIsImporting(false);
        }
    };

    const handleClose = () => {
        setCsvData([]);
        setImportProgress(0);
        setIsImporting(false);
        setSelectedFields([]);
        onHide();
    };

    const toggleField = (field) => {
        setSelectedFields(prev => {
            const exists = prev.find(f => f.name === field.name);
            if (exists) {
                return prev.filter(f => f.name !== field.name);
            } else {
                return [...prev, field];
            }
        });
    };

    const selectAllFields = () => {
        setSelectedFields(availableFields);
    };

    const selectRequiredFields = () => {
        setSelectedFields(availableFields.filter(field => field.required));
    };

    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">
                            <i className="bi bi-upload me-2"></i>
                            Import Salary Settings
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleClose}
                            disabled={isImporting}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <div className="row g-4">
                            {/* Field Selection */}
                            <div className="col-12">
                                <div className="card border-primary">
                                    <div className="card-header bg-primary text-white">
                                        <h6 className="mb-0">
                                            <i className="bi bi-list-check me-2"></i>
                                            Step 1: Select Fields to Import
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <small className="text-muted">
                                                Select the salary setting fields you want to include in the import template.
                                                <span className="text-danger fw-bold ms-2">emp_code is required</span>
                                            </small>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <button
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={selectAllFields}
                                                disabled={selectedFields.length === availableFields.length}
                                            >
                                                <i className="bi bi-check-all me-1"></i>
                                                Select All
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary me-2"
                                                onClick={selectRequiredFields}
                                            >
                                                <i className="bi bi-star me-1"></i>
                                                Required Only
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => setSelectedFields([])}
                                                disabled={selectedFields.length === 0}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>
                                                Clear All
                                            </button>
                                        </div>

                                        <div className="row g-2">
                                            {availableFields.map(field => (
                                                <div key={field.name} className="col-md-6 col-lg-4">
                                                    <div className={`form-check card border ${field.required ? 'border-danger' : ''}`}>
                                                        <div className="card-body py-2">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`field-${field.name}`}
                                                                checked={selectedFields.some(f => f.name === field.name)}
                                                                onChange={() => toggleField(field)}
                                                                disabled={field.required && selectedFields.some(f => f.name === field.name)}
                                                            />
                                                            <label className="form-check-label ms-2" htmlFor={`field-${field.name}`}>
                                                                <strong>
                                                                    {field.label}
                                                                    {field.required && <span className="text-danger ms-1">*</span>}
                                                                </strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    <span className="badge bg-secondary">{field.name}</span>
                                                                    {' '}
                                                                    <span className="badge bg-info">{field.type}</span>
                                                                    {field.options && (
                                                                        <>
                                                                            <br />
                                                                            <span className="text-muted">
                                                                                Options: {field.options.slice(0, 3).join(', ')}
                                                                                {field.options.length > 3 ? '...' : ''}
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </small>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Template Download */}
                            {selectedFields.length > 0 && (
                                <div className="col-12">
                                    <div className="card border-success">
                                        <div className="card-header bg-success text-white">
                                            <h6 className="mb-0">
                                                <i className="bi bi-download me-2"></i>
                                                Step 2: Download Template
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-md-8">
                                                    <h6 className="text-success">
                                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                                        Salary Settings Template
                                                    </h6>
                                                    <p className="mb-2">
                                                        Template will include columns for: 
                                                        <strong className="ms-1">
                                                            {selectedFields.map(f => f.name).join(', ')}
                                                        </strong>
                                                    </p>
                                                    <p className="mb-0 text-muted small">
                                                        <i className="bi bi-info-circle me-1"></i>
                                                        Each row represents salary settings for one employee identified by emp_code
                                                    </p>
                                                </div>
                                                <div className="col-md-4 text-end">
                                                    <button
                                                        className="btn btn-success btn-lg"
                                                        onClick={downloadTemplate}
                                                    >
                                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                                        Download Template
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* File Upload */}
                            {selectedFields.length > 0 && (
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0">
                                                <i className="bi bi-upload me-2"></i>
                                                Step 3: Upload Filled Template
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <label className="form-label">Select File *</label>
                                            <div className="input-group">
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept=".csv,.xlsx,.xls"
                                                    onChange={handleFileUpload}
                                                    disabled={isImporting}
                                                />
                                                <button
                                                    className="btn btn-outline-success"
                                                    type="button"
                                                    onClick={downloadTemplate}
                                                    title="Download Template"
                                                    disabled={isImporting}
                                                >
                                                    <i className="bi bi-download me-1"></i>
                                                    Template
                                                </button>
                                            </div>
                                            <div className="form-text">
                                                Supports CSV, XLSX, XLS files. Must match the downloaded template structure.
                                                <span className="text-danger fw-bold"> emp_code is required for each record.</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Data Preview */}
                            {csvData.length > 0 && (
                                <div className="col-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="card-title mb-0">
                                                    <i className="bi bi-table me-2"></i>
                                                    Preview ({csvData.length} records found)
                                                </h6>
                                                <span className="badge bg-primary">
                                                    Flat File Format
                                                </span>
                                            </div>
                                            <div className="table-responsive" style={{ maxHeight: '250px' }}>
                                                <table className="table table-sm table-bordered">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            {Object.keys(csvData[0]).map(key => (
                                                                <th key={key} className="small">
                                                                    {key === 'emp_code' ? (
                                                                        <span className="text-danger">{key}*</span>
                                                                    ) : (
                                                                        key
                                                                    )}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {csvData.slice(0, 5).map((row, index) => (
                                                            <tr key={index}>
                                                                {Object.entries(row).map(([key, value], i) => (
                                                                    <td
                                                                        key={i}
                                                                        className="small"
                                                                        style={{
                                                                            color: key === 'emp_code' && !value ? 'red' : 'inherit'
                                                                        }}
                                                                    >
                                                                        {key === 'emp_code' && !value ? (
                                                                            <span className="text-danger">Required!</span>
                                                                        ) : (
                                                                            value || ''
                                                                        )}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {csvData.length > 5 && (
                                                <div className="text-center small text-muted mt-2">
                                                    <i className="bi bi-arrow-down"></i>
                                                    ... and {csvData.length - 5} more employees
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Import Progress */}
                            {isImporting && (
                                <div className="col-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="card-title">
                                                <i className="bi bi-arrow-repeat me-2"></i>
                                                Importing Salary Settings...
                                            </h6>
                                            <div className="progress mb-2">
                                                <div
                                                    className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                                                    style={{ width: `${importProgress}%` }}
                                                >
                                                    {importProgress}%
                                                </div>
                                            </div>
                                            <small className="text-muted">
                                                Updating salary settings for employees. Please wait...
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Error Display */}
                            {error && (
                                <div className="col-12">
                                    <div className="alert alert-danger">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={isImporting}
                        >
                            Cancel
                        </button>
                        {selectedFields.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={downloadTemplate}
                                disabled={isImporting}
                            >
                                <i className="bi bi-download me-2"></i>
                                Get Template
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleImport}
                            disabled={!csvData.length || isImporting || selectedFields.length === 0}
                        >
                            {isImporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-upload me-2"></i>
                                    Start Import
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportSalarySettings;
